<script setup>
import { computed, onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import { useModalStore } from '@state/stores/modal'

const modalStore = useModalStore()

const loadedComponent = shallowRef(null)
const isLoading = ref(false)

const isModalOpen = computed(() => modalStore.isOpen)
const currentModal = computed(() => modalStore.currentModal)

function hideModal() {
    return modalStore.hideModal()
}

async function loadModalComponent() {
    if (!currentModal.value.component) {
        console.error('No component found in currentModal')
        return
    }

    isLoading.value = true
    try {
        const module = await currentModal.value.component()
        loadedComponent.value = module.default
    } catch (error) {
        console.error('Failed to load modal component:', error)
        hideModal()
    } finally {
        isLoading.value = false
    }
}

function lockBodyScroll() {
    const scrollY = window.scrollY
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.width = '100%'
    document.body.dataset.scrollY = scrollY
}

function unlockBodyScroll() {
    const scrollY = document.body.dataset.scrollY || '0'
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''
    window.scrollTo(0, parseInt(scrollY, 10))
    delete document.body.dataset.scrollY
}

function handleEscape(event) {
    if (event.key === 'Escape' && isModalOpen.value) {
        hideModal()
    }
}

function addEscapeListener() {
    document.addEventListener('keydown', handleEscape)
}

function removeEscapeListener() {
    document.removeEventListener('keydown', handleEscape)
}

function handleBackgroundClick() {
    hideModal()
}

watch(
    isModalOpen,
    isOpen => {
        if (isOpen) {
            lockBodyScroll()
            loadModalComponent()
            addEscapeListener()
        } else {
            unlockBodyScroll()
            loadedComponent.value = null
            removeEscapeListener()
        }
    },
    { immediate: true }
)

onBeforeUnmount(() => {
    unlockBodyScroll()
    removeEscapeListener()
})
</script>

<template>
    <transition name="modal-fade">
        <div
            v-if="isModalOpen"
            class="modal-container"
        >
            <div
                class="modal-overlay"
                @click="handleBackgroundClick"
            />
            <div
                v-if="!isLoading && loadedComponent"
                class="modal-wrapper"
                role="dialog"
                aria-modal="true"
            >
                <component
                    :is="loadedComponent"
                    :data="currentModal.data"
                    @close="hideModal"
                />
            </div>
        </div>
    </transition>
</template>

<style scoped lang="scss">
@use '@design' as *;

.modal-container {
    position: fixed;
    top: 0;
    left: 0;
    z-index: $layer-modal-z-index;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: $s-m;

    @include mobileOnly {
        padding: $s-l;
    }
}

.modal-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
    background-color: rgba(0, 0, 0, 0.75);
}

.modal-wrapper {
    position: relative;
    z-index: 1;
    max-width: 100%;
    max-height: 100%;
    overflow: auto;

    @include mobileOnly {
        display: flex;
        align-items: center;
        justify-content: center;
        width: auto; // Auto width to allow the modal to size itself
        height: auto; // Auto height
    }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;
}

.modal-fade-enter-active .modal-wrapper,
.modal-fade-leave-active .modal-wrapper {
    transition: transform 0.2s ease;
}

.modal-fade-enter-from .modal-wrapper,
.modal-fade-leave-to .modal-wrapper {
    transform: scale(0.95);
}
</style>
